import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2, RefreshCw, FolderOpen, AlertTriangle, ExternalLink,
  CheckCircle2, XCircle, HelpCircle, Pause, Archive, Send, FileSearch, Gauge,
} from "lucide-react";
import { toast } from "sonner";
import { adminReviewFolder } from "../../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const STATUS_META = {
  investigating: { label: "INVESTIGATING",   color: "#E0A526", icon: Loader2 },
  needs_review:  { label: "NEEDS REVIEW",    color: "#D92D20", icon: FileSearch },
  approved:      { label: "APPROVED",        color: "#5DD4B3", icon: CheckCircle2 },
  denied:        { label: "DENIED",          color: "#FFA9A0", icon: XCircle },
  undetermined:  { label: "UNDETERMINED",    color: "#CBB892", icon: HelpCircle },
  hold:          { label: "ON HOLD",         color: "#9FA8D4", icon: Pause },
  archived:      { label: "ARCHIVED",        color: "#757065", icon: Archive },
  published:     { label: "PUBLISHED",       color: "#5DD4B3", icon: Send },
};

const TABS = [
  { key: "needs_review",  label: "Needs review" },
  { key: "investigating", label: "Investigating" },
  { key: "approved",      label: "Approved" },
  { key: "hold",          label: "On hold" },
  { key: "undetermined",  label: "Undetermined" },
  { key: "denied",        label: "Denied" },
  { key: "archived",      label: "Archived" },
  { key: "published",     label: "Published" },
];

const CONF_COLOR = {
  strong_source_base: "#5DD4B3",
  mixed_source_base:  "#CBB892",
  weak_source_base:   "#D92D20",
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.needs_review;
  const Icon = m.icon;
  return (
    <span
      style={{ color: m.color, borderColor: m.color }}
      className="inline-flex items-center gap-1 px-2 py-0.5 border font-mono text-[9px] uppercase tracking-[0.25em]"
      data-testid={`review-status-${status}`}
    >
      {Icon && <Icon size={10} className={status === "investigating" ? "animate-spin" : ""} />}
      {m.label}
    </span>
  );
};

const fmtTs = (iso) => iso ? new Date(iso).toLocaleString() : "";

/* Eight-dimension Intelligence Score — reuses /api/command/intel, weakest
   dimensions first, each weak dimension links to its editorial action. */
const IntelScorePanel = ({ slug, token }) => {
  const [open, setOpen] = useState(false);
  const [intel, setIntel] = useState(null);
  const [err, setErr] = useState(false);

  const load = () => {
    if (intel || err) { setOpen((v) => !v); return; }
    setOpen(true);
    axios.get(`${API}/command/intel/investigation/${slug}`,
              { headers: { "x-admin-token": token } })
      .then((r) => {
        if ((r.data?.card?.dims || []).length) setIntel(r.data);
        else setErr(true);
      })
      .catch(() => setErr(true));
  };

  const dims = [...((intel?.card?.dims) || [])].sort((a, b) => a.score - b.score);
  return (
    <div className="mt-3">
      <button type="button" onClick={load} data-testid={`intel-score-toggle-${slug}`}
              className="inline-flex items-center gap-1.5 border border-[#2D2A26] px-2 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065] hover:border-[#D92D20]/50 hover:text-[#A39E93]">
        <Gauge size={10} /> Intel Score {open ? "▾" : "▸"}
      </button>
      {open && (
        <div className="mt-2 border border-[#2D2A26] bg-[#0a0806] px-3 py-2 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-1.5"
             data-testid={`intel-score-panel-${slug}`}>
          {err && (
            <div className="font-mono text-[9px] text-[#757065] col-span-full">
              No published intelligence record for this case yet.
            </div>
          )}
          {!err && !intel && (
            <div className="font-mono text-[9px] text-[#757065] col-span-full animate-pulse">
              Computing operational dimensions…
            </div>
          )}
          {dims.map((d) => {
            const c = d.score >= 70 ? "#5DD4B3" : d.score >= 40 ? "#E0A526" : "#D92D20";
            return (
              <div key={d.label}>
                <div className="flex justify-between font-mono text-[8px] uppercase tracking-[0.12em] text-[#757065]">
                  <span>{d.label}</span>
                  <span style={{ color: c }}>{d.score}</span>
                </div>
                <div className="h-[3px] bg-[#1C1917] mt-0.5">
                  <div className="h-full" style={{ width: `${Math.min(100, d.score)}%`, background: c }} />
                </div>
                {d.action && (
                  <Link to={d.action.url}
                        className="font-mono text-[8px] uppercase tracking-[0.1em] text-[#D92D20]/80 hover:text-[#D92D20]">
                    {d.action.label} →
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const AdminReviewFolder = ({ token, userToken = null }) => {
  const [tab, setTab] = useState("needs_review");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!token && !userToken) return;
    setLoading(true);
    try {
      const r = await adminReviewFolder(token,
        { editorial_status_filter: tab, limit: 200 }, userToken);
      setItems(r.drafts || []);
      setCounts(r.counts || {});
    } catch {
      toast.error("Failed to load Review Folder");
    } finally { setLoading(false); }
  }, [token, userToken, tab]);

  useEffect(() => { load(); }, [load]);

  // Poll while any row is investigating
  useEffect(() => {
    const investigating = (counts.investigating || 0) > 0;
    if (!investigating) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [counts, load]);

  return (
    <div data-testid="admin-review-folder">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <FolderOpen size={13} /> // REVIEW FOLDER
          </div>
          <h1 className="font-display text-4xl text-[#E4DFD3]">Investigation case files</h1>
          <p className="text-[#A39E93] mt-1 text-sm max-w-2xl">
            Every AI-investigated submission lands here as a case file awaiting your
            editorial sign-off. Approve, deny, mark undetermined, put on hold, or archive.
            Publishing is still a separate explicit action on the case file itself.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="review-refresh-btn"
          className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 my-4 border-b border-[#2D2A26]">
        {TABS.map((t) => {
          const c = counts[t.key] || 0;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`review-tab-${t.key}`}
              className={
                "px-4 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 " +
                (active ? "border-[#D92D20] text-[#D92D20]" : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")
              }
            >
              {t.label}{c ? <span className="ml-1 text-[#757065]">·&nbsp;{c}</span> : null}
            </button>
          );
        })}
      </div>

      {loading && items.length === 0 ? (
        <p className="font-mono text-xs uppercase tracking-widest text-[#757065] py-8">Loading…</p>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[#2D2A26] p-16 text-center" data-testid="review-empty">
          <p className="font-mono text-xs uppercase tracking-widest text-[#757065]">
            Nothing in this view. Investigated submissions will land here as case files.
          </p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4" data-testid="review-grid">
          {items.map((d) => {
            const confColor = CONF_COLOR[d.confidence] || "#A39E93";
            return (
              <div key={d.id}
                   data-testid={`review-card-${d.id}`}
                   className="border border-[#2D2A26] bg-[#131313] p-5 aged-paper">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <StatusBadge status={d.editorial_status} />
                  {d.confidence && (
                    <span
                      style={{ color: confColor, borderColor: confColor }}
                      className="inline-flex items-center px-2 py-0.5 border font-mono text-[9px] uppercase tracking-[0.25em]"
                    >
                      {d.confidence.replace(/_/g, " ")}
                    </span>
                  )}
                  {d.duplicate_verdict && d.duplicate_verdict !== "novel" && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#CBB892]/40 text-[#CBB892] font-mono text-[9px] uppercase tracking-[0.25em]">
                      <AlertTriangle size={9} /> AI DUPE: {d.duplicate_verdict}
                    </span>
                  )}
                  {d.category && (
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">
                      CAT: {d.category}
                    </span>
                  )}
                </div>
                <h3 className="font-heading text-xl text-[#E4DFD3] mb-2 leading-tight">
                  {d.title}
                </h3>
                {d.submission_title && d.submission_title !== d.title && (
                  <div className="font-mono text-[10px] text-[#757065] mb-2">
                    // submitted as: <span className="text-[#A39E93]">{d.submission_title}</span>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-3 my-3 text-center">
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">SOURCES</div>
                    <div className="font-display text-xl text-[#E4DFD3]">{d.source_count}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">CORE CLAIMS</div>
                    <div className="font-display text-xl text-[#E4DFD3]">{d.core_claim_count}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">CONTRADICTIONS</div>
                    <div className={"font-display text-xl " + (d.contradiction_count ? "text-[#D92D20]" : "text-[#5DD4B3]")}>
                      {d.contradiction_count}
                    </div>
                  </div>
                </div>
                <div className="font-mono text-[10px] text-[#757065] border-t border-[#2D2A26] pt-3 mt-3 flex flex-wrap gap-3">
                  <span>tracking <code className="text-[#A39E93]">{d.tracking_id}</code></span>
                  {d.submitter && <span>by <span className="text-[#A39E93]">{d.submitter}</span></span>}
                  <span>updated {fmtTs(d.updated_at)}</span>
                </div>
                {d.error && (
                  <div className="mt-2 border border-[#D92D20]/40 bg-[#D92D20]/5 px-2 py-1 font-mono text-[10px] text-[#FFA9A0]">
                    AI error: {d.error}
                  </div>
                )}
                {d.published_topic_slug && (
                  <IntelScorePanel slug={d.published_topic_slug} token={token} />
                )}
                <div className="mt-4 flex gap-2">
                  <Link
                    to={`/admin/investigation/${d.id}`}
                    data-testid={`open-review-${d.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
                  >
                    <FileSearch size={11} /> Open case file <ExternalLink size={10} />
                  </Link>
                  {d.published_topic_id && (
                    <Link
                      to={`/topic/${d.published_topic_slug || d.published_topic_id}`}
                      onClick={(e) => { e.preventDefault(); window.open(`/topic/${d.published_topic_slug || d.published_topic_id}`, "_blank"); }}
                      className="border border-[#5DD4B3] text-[#5DD4B3] hover:bg-[#5DD4B3]/10 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] inline-flex items-center gap-1"
                    >
                      Public
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminReviewFolder;
